import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  UserPlus, Plus, Trash2, Search, Calendar, MapPin, Briefcase, GraduationCap,
  Car, User, Star, Upload, FileText, X, Filter, Pencil, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  tarih: new Date().toISOString().split('T')[0],
  isim: '',
  soyisim: '',
  meslek: '',
  egitim: '',
  yasadigi_yer: '',
  ehliyet: '',
  yas: '',
  cinsiyet: '',
  notlar: '',
  puan: 0
});

const StarRating = ({ value = 0, onChange, readOnly = false, size = 'md', testIdPrefix = 'star' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  return (
    <div className="flex items-center gap-0.5" data-testid={`${testIdPrefix}-rating`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(i)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={(e) => {
              e.stopPropagation();
              if (readOnly) return;
              onChange && onChange(value === i ? 0 : i);
            }}
            className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'} p-0.5`}
            data-testid={`${testIdPrefix}-${i}`}
          >
            <Star className={`${sizeClass} ${active ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
          </button>
        );
      })}
    </div>
  );
};

const TalentPool = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [professionFilter, setProfessionFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [pendingFiles, setPendingFiles] = useState([]); // files chosen in modal (for new CV)
  const [uploadingFor, setUploadingFor] = useState(null);
  const [puanSort, setPuanSort] = useState('none'); // 'none' | 'desc' | 'asc'
  const fileInputRef = useRef(null);
  const modalFileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  const getMultipartHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`
  });

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/talent-pool`, { headers: getAuthHeaders() });
      setCandidates(res.data);
    } catch (err) {
      console.error('Talent pool yüklenemedi:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setPendingFiles([]);
    setShowAddModal(true);
  };

  const openEditModal = (c) => {
    setEditingId(c.id);
    setFormData({
      tarih: c.tarih || new Date().toISOString().split('T')[0],
      isim: c.isim || '',
      soyisim: c.soyisim || '',
      meslek: c.meslek || '',
      egitim: c.egitim || '',
      yasadigi_yer: c.yasadigi_yer || '',
      ehliyet: c.ehliyet || '',
      yas: c.yas || '',
      cinsiyet: c.cinsiyet || '',
      notlar: c.notlar || '',
      puan: c.puan || 0
    });
    setPendingFiles([]);
    setShowAddModal(true);
  };

  const uploadFilesForCandidate = async (candidateId, files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await axios.post(`${API}/api/talent-pool/${candidateId}/documents`, fd, {
          headers: getMultipartHeaders()
        });
      } catch (err) {
        toast.error(`${file.name} yüklenemedi: ${err.response?.data?.detail || 'hata'}`);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.isim.trim() || !formData.soyisim.trim()) {
      toast.error('İsim ve soyisim zorunludur');
      return;
    }
    try {
      let candidateId = editingId;
      if (editingId) {
        await axios.put(`${API}/api/talent-pool/${editingId}`, formData, { headers: getAuthHeaders() });
        toast.success('CV güncellendi');
      } else {
        const res = await axios.post(`${API}/api/talent-pool`, formData, { headers: getAuthHeaders() });
        candidateId = res.data.id;
        toast.success('CV başarıyla eklendi');
      }
      if (pendingFiles.length > 0 && candidateId) {
        await uploadFilesForCandidate(candidateId, pendingFiles);
        toast.success(`${pendingFiles.length} belge yüklendi`);
      }
      setShowAddModal(false);
      setEditingId(null);
      setPendingFiles([]);
      setFormData(emptyForm());
      fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CV kaydedilemedi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/talent-pool/${id}`, { headers: getAuthHeaders() });
      toast.success('Kayıt silindi');
      fetchCandidates();
    } catch (err) {
      toast.error('Kayıt silinemedi');
    }
  };

  const handleRatingInlineChange = async (candidate, newValue) => {
    try {
      await axios.put(
        `${API}/api/talent-pool/${candidate.id}`,
        { puan: newValue },
        { headers: getAuthHeaders() }
      );
      setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, puan: newValue } : c));
    } catch (err) {
      toast.error('Puan güncellenemedi');
    }
  };

  const togglePuanSort = () => {
    setPuanSort(prev => prev === 'none' ? 'desc' : prev === 'desc' ? 'asc' : 'none');
  };

  const handleQuickUpload = async (candidate, files) => {
    if (!files || files.length === 0) return;
    setUploadingFor(candidate.id);
    try {
      await uploadFilesForCandidate(candidate.id, Array.from(files));
      toast.success(`${files.length} belge yüklendi`);
      fetchCandidates();
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDeleteDoc = async (candidateId, docId) => {
    if (!window.confirm('Bu belgeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/talent-pool/${candidateId}/documents/${docId}`, { headers: getAuthHeaders() });
      toast.success('Belge silindi');
      fetchCandidates();
    } catch (err) {
      toast.error('Belge silinemedi');
    }
  };

  const professions = useMemo(() => {
    const set = new Set();
    candidates.forEach(c => { if (c.meslek && c.meslek.trim()) set.add(c.meslek.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [candidates]);

  const filtered = candidates.filter(c => {
    if (professionFilter !== 'all' && (c.meslek || '').trim() !== professionFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.isim || '').toLowerCase().includes(s) ||
      (c.soyisim || '').toLowerCase().includes(s) ||
      (c.meslek || '').toLowerCase().includes(s) ||
      (c.yasadigi_yer || '').toLowerCase().includes(s);
  });

  const displayed = useMemo(() => {
    if (puanSort === 'none') return filtered;
    const sorted = [...filtered].sort((a, b) => {
      const pa = a.puan || 0;
      const pb = b.puan || 0;
      return puanSort === 'desc' ? pb - pa : pa - pb;
    });
    return sorted;
  }, [filtered, puanSort]);

  return (
    <div className="space-y-6" data-testid="talent-pool-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Talent Pool</h1>
        <Button onClick={openAddModal} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="add-cv-btn">
          <Plus className="h-4 w-4" />
          CV Ekle
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              Aday Havuzu
              <Badge variant="outline">{filtered.length}</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Filter className="h-4 w-4 text-gray-500" />
                <Select value={professionFilter} onValueChange={setProfessionFilter}>
                  <SelectTrigger className="w-48" data-testid="profession-filter">
                    <SelectValue placeholder="Meslek filtrele" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Meslekler</SelectItem>
                    {professions.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="İsim, meslek, yer ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="talent-search"
                />
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <UserPlus className="h-16 w-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">
                {search || professionFilter !== 'all' ? 'Sonuç bulunamadı' : 'Henüz aday eklenmemiş'}
              </p>
              {!search && professionFilter === 'all' && (
                <Button variant="outline" className="mt-4" onClick={openAddModal}>
                  <Plus className="h-4 w-4 mr-2" />
                  İlk CV'yi Ekle
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>İsim Soyisim</TableHead>
                  <TableHead>Meslek</TableHead>
                  <TableHead>Eğitim</TableHead>
                  <TableHead>Yaşadığı Yer</TableHead>
                  <TableHead>Ehliyet</TableHead>
                  <TableHead>Yaş</TableHead>
                  <TableHead>Cinsiyet</TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={togglePuanSort}
                      className="inline-flex items-center gap-1 hover:text-indigo-600 transition-colors"
                      data-testid="puan-sort-btn"
                      title={puanSort === 'none' ? 'Yüksekten düşüğe sırala' : puanSort === 'desc' ? 'Düşükten yükseğe sırala' : 'Sıralamayı kaldır'}
                    >
                      Uyumluluk
                      {puanSort === 'none' && <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />}
                      {puanSort === 'desc' && <ArrowDown className="h-3.5 w-3.5 text-indigo-600" />}
                      {puanSort === 'asc' && <ArrowUp className="h-3.5 w-3.5 text-indigo-600" />}
                    </button>
                  </TableHead>
                  <TableHead>Belgeler</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map((c) => (
                  <TableRow key={c.id} data-testid={`talent-row-${c.id}`}>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {c.tarih ? new Date(c.tarih).toLocaleDateString('tr-TR') : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{c.isim} {c.soyisim}</TableCell>
                    <TableCell>
                      {c.meslek ? <Badge variant="outline" className="bg-blue-50 text-blue-700">{c.meslek}</Badge> : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{c.egitim || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {c.yasadigi_yer ? <div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-gray-400" />{c.yasadigi_yer}</div> : '-'}
                    </TableCell>
                    <TableCell className="text-sm">{c.ehliyet || '-'}</TableCell>
                    <TableCell className="text-sm">{c.yas || '-'}</TableCell>
                    <TableCell className="text-sm">{c.cinsiyet || '-'}</TableCell>
                    <TableCell>
                      <StarRating
                        value={c.puan || 0}
                        readOnly={true}
                        size="sm"
                        testIdPrefix={`row-${c.id}-star`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 max-w-[220px]">
                        {(c.belgeler || []).map((d) => (
                          <div key={d.id} className="flex items-center gap-1 text-xs bg-gray-50 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-indigo-600 flex-shrink-0" />
                            <a
                              href={`${API}${d.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 truncate text-indigo-700 hover:underline"
                              title={d.original_filename}
                              data-testid={`doc-link-${d.id}`}
                            >
                              {d.original_filename}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteDoc(c.id, d.id)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-doc-${d.id}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <label className="inline-flex">
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              handleQuickUpload(c, e.target.files);
                              e.target.value = '';
                            }}
                            data-testid={`row-upload-${c.id}`}
                          />
                          <span className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer">
                            <Upload className="h-3 w-3" />
                            {uploadingFor === c.id ? 'Yükleniyor...' : 'Belge Ekle'}
                          </span>
                        </label>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(c)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid={`edit-talent-${c.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(c.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-talent-${c.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* CV Ekle / Düzenle Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) { setEditingId(null); setPendingFiles([]); }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              {editingId ? 'CV Düzenle' : 'CV Ekle'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Tarih</Label>
              <Input type="date" value={formData.tarih} onChange={(e) => setFormData(prev => ({ ...prev, tarih: e.target.value }))} data-testid="cv-tarih" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1"><User className="h-3 w-3" /> İsim *</Label>
                <Input value={formData.isim} onChange={(e) => setFormData(prev => ({ ...prev, isim: e.target.value }))} placeholder="İsim" data-testid="cv-isim" />
              </div>
              <div>
                <Label>Soyisim *</Label>
                <Input value={formData.soyisim} onChange={(e) => setFormData(prev => ({ ...prev, soyisim: e.target.value }))} placeholder="Soyisim" data-testid="cv-soyisim" />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1"><Briefcase className="h-3 w-3" /> Meslek</Label>
              <Input value={formData.meslek} onChange={(e) => setFormData(prev => ({ ...prev, meslek: e.target.value }))} placeholder="Meslek" data-testid="cv-meslek" />
            </div>

            <div>
              <Label className="flex items-center gap-1"><GraduationCap className="h-3 w-3" /> Eğitim</Label>
              <Input value={formData.egitim} onChange={(e) => setFormData(prev => ({ ...prev, egitim: e.target.value }))} placeholder="Eğitim durumu" data-testid="cv-egitim" />
            </div>

            <div>
              <Label className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Yaşadığı Yer</Label>
              <Input value={formData.yasadigi_yer} onChange={(e) => setFormData(prev => ({ ...prev, yasadigi_yer: e.target.value }))} placeholder="Şehir / İlçe" data-testid="cv-yer" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="flex items-center gap-1"><Car className="h-3 w-3" /> Ehliyet</Label>
                <Select value={formData.ehliyet} onValueChange={(val) => setFormData(prev => ({ ...prev, ehliyet: val }))}>
                  <SelectTrigger data-testid="cv-ehliyet"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yok">Yok</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                    <SelectItem value="E">E</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Yaş</Label>
                <Input type="number" min="16" max="99" value={formData.yas} onChange={(e) => setFormData(prev => ({ ...prev, yas: e.target.value }))} placeholder="Yaş" data-testid="cv-yas" />
              </div>
              <div>
                <Label>Cinsiyet</Label>
                <Select value={formData.cinsiyet} onValueChange={(val) => setFormData(prev => ({ ...prev, cinsiyet: val }))}>
                  <SelectTrigger data-testid="cv-cinsiyet"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Erkek">Erkek</SelectItem>
                    <SelectItem value="Kadın">Kadın</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1"><Star className="h-3 w-3" /> Uyumluluk Puanı (1-5)</Label>
              <div className="mt-1">
                <StarRating
                  value={formData.puan}
                  onChange={(val) => setFormData(prev => ({ ...prev, puan: val }))}
                  size="lg"
                  testIdPrefix="cv-puan"
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1"><FileText className="h-3 w-3" /> Belgeler</Label>
              <div className="mt-1 space-y-2">
                <input
                  ref={modalFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setPendingFiles(prev => [...prev, ...files]);
                    e.target.value = '';
                  }}
                  data-testid="cv-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => modalFileInputRef.current?.click()}
                  className="gap-2 w-full"
                  data-testid="cv-add-doc-btn"
                >
                  <Upload className="h-4 w-4" />
                  Belge Ekle
                </Button>
                {pendingFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1">
                        <FileText className="h-3 w-3 text-indigo-600" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                        <button
                          type="button"
                          onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">{pendingFiles.length} belge kaydet ile yüklenecek</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Notlar</Label>
              <Textarea value={formData.notlar} onChange={(e) => setFormData(prev => ({ ...prev, notlar: e.target.value }))} placeholder="Ek notlar..." rows={2} data-testid="cv-notlar" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>İptal</Button>
            <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-cv-btn">
              {editingId ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentPool;
